import { Form } from 'radix-ui'
import { Button, Container, Flex, Section, Text, TextArea, TextField } from '@radix-ui/themes'
import { FileUploader, FileWithPreview } from '@Components/common/file-uploader'
import { useMutation } from '@tanstack/react-query'
import { CouponServiceAPI } from '@API/data-services/coupon-service'
import { RootState } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { useRef, useState } from 'react'
import { parseISO } from 'date-fns'
import { convertDateToIsoDate } from '@Components/util/utils'
import { useNavigate } from 'react-router-dom'
import { Routes } from '@Components/main-app/types'

type ValidationError = {
  startDate: boolean
  endDate: boolean
  expirationDate: boolean
  description: boolean
  couponImageFile: boolean
  couponFile: boolean
}

export const CreateCouponPromotion = () => {
  const startDateRef = useRef<HTMLInputElement>(null)
  const endDateRef = useRef<HTMLInputElement>(null)
  const expirationDateRef = useRef<HTMLInputElement>(null)
  const nav = useNavigate()
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [validationErrors, setValidationErrors] = useState<ValidationError>({
    startDate: false,
    endDate: false,
    expirationDate: false,
    description: false,
    couponImageFile: false,
    couponFile: false,
  })
  const [couponFile, setCouponFile] = useState<FileWithPreview>()
  const [couponImageFile, setCouponImageFile] = useState<FileWithPreview>()

  const {
    mutate: createCouponPromotion,
    isPending,
    error,
  } = useMutation({
    mutationFn: ({
      promotionName,
      promotionDescription,
      startDate,
      endDate,
      expirationDate,
      couponFile,
      templateImageFile,
    }: {
      promotionName: string
      promotionDescription: string
      startDate: string
      endDate: string
      expirationDate: string
      couponFile: Blob
      templateImageFile: Blob
    }) => {
      return CouponServiceAPI.createCouponPromotion({
        customerId: activeCustomerId!,
        promotionName,
        promotionDescription,
        startDate,
        endDate,
        expirationDate,
        couponFile,
        templateImageFile,
      })
    },
    onSuccess: () => {
      nav(Routes.COUPON_PROMOTIONS)
    },
  })

  return (
    <Container>
      <Section size="2">
        <Flex direction="column" gap="2">
          <Form.Root
            onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.currentTarget).entries()
              const data = Object.fromEntries(formData)

              const errors: ValidationError = {
                startDate: !data.startDate,
                endDate: !data.endDate,
                expirationDate: !data.expirationDate,
                description: !data.description,
                couponImageFile: !couponImageFile,
                couponFile: !couponFile,
              }

              const hasError = Object.values(errors).some((e) => e)
              if (hasError) {
                setValidationErrors(errors)
                return
              }

              createCouponPromotion({
                promotionName: data.promotionName as string,
                promotionDescription: data.description as string,
                startDate: convertDateToIsoDate(parseISO(data.startDate as string)),
                endDate: convertDateToIsoDate(parseISO(data.endDate as string)),
                expirationDate: data.expirationDate as string,
                couponFile: couponFile!.file,
                templateImageFile: couponImageFile!.file,
              })
            }}>
            <Flex direction="column" gap="4">
              <Form.Field name="promotionName">
                <Flex justify="between">
                  <Form.Label>Promotion Name</Form.Label>
                  <Form.Message className="FormMessage" match="valueMissing">
                    Promotion Name is required
                  </Form.Message>
                </Flex>

                <Form.Control asChild required>
                  <TextField.Root />
                </Form.Control>
              </Form.Field>

              <Form.Field name="startDate" className="form-field">
                <Flex justify="between">
                  <Form.Label>Start Date</Form.Label>
                  {validationErrors.startDate ? (
                    <Form.Message>Please provide a start date.</Form.Message>
                  ) : null}
                </Flex>
                <Form.Control
                  ref={startDateRef}
                  asChild
                  type="date"
                  className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                  onFocus={() => startDateRef.current?.showPicker()}
                  onChange={() => {
                    setValidationErrors({ ...validationErrors, startDate: false })
                  }}>
                  <TextField.Root />
                </Form.Control>
              </Form.Field>

              <Form.Field name="endDate" className="form-field">
                <Flex justify="between">
                  <Form.Label>End Date</Form.Label>
                  {validationErrors.endDate ? (
                    <Form.Message>Please provide an end date.</Form.Message>
                  ) : null}
                </Flex>
                <Form.Control
                  ref={endDateRef}
                  asChild
                  type="date"
                  className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                  onFocus={() => endDateRef.current?.showPicker()}
                  onChange={() => {
                    setValidationErrors({ ...validationErrors, endDate: false })
                  }}>
                  <TextField.Root />
                </Form.Control>
              </Form.Field>

              <Form.Field name="expirationDate" className="form-field">
                <Flex justify="between">
                  <Form.Label>Expiration Date</Form.Label>
                  {validationErrors.expirationDate ? (
                    <Form.Message>Please provide an expiration date.</Form.Message>
                  ) : null}
                </Flex>
                <Form.Control
                  ref={expirationDateRef}
                  asChild
                  type="date"
                  className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                  onFocus={() => expirationDateRef.current?.showPicker()}
                  onChange={() => {
                    setValidationErrors({ ...validationErrors, expirationDate: false })
                  }}>
                  <TextField.Root />
                </Form.Control>
              </Form.Field>

              <Form.Field name="description">
                <Flex justify="between">
                  <Form.Label>Description</Form.Label>
                  <Form.Message className="FormMessage" match="valueMissing">
                    Description is required
                  </Form.Message>
                </Flex>

                <Form.Control asChild required>
                  <TextArea />
                </Form.Control>
              </Form.Field>

              <FileUploader
                onFileDropped={(file) => {
                  setCouponImageFile(file)
                  setValidationErrors({ ...validationErrors, couponImageFile: false })
                }}
                formLabel="Coupon Template Image"
                errorMessage={
                  validationErrors.couponImageFile ? 'Please provide a template image' : null
                }
              />

              <FileUploader
                onFileDropped={(file) => {
                  setCouponFile(file)
                  setValidationErrors({ ...validationErrors, couponImageFile: false })
                }}
                formLabel="Coupon Barcode File"
                acceptTypes={{}}
                showImageAsPreview={false}
                errorMessage={
                  validationErrors.couponImageFile ? 'Please provide a coupon file' : null
                }
              />

              <Form.Submit asChild>
                <Button disabled={isPending} loading={isPending}>
                  Submit
                </Button>
              </Form.Submit>
              {error ? (
                <Text align="center" color="red">
                  There was an error creating the coupon promotion: {error?.message}
                </Text>
              ) : null}
            </Flex>
          </Form.Root>
        </Flex>
      </Section>
    </Container>
  )
}
